<template>
  <VaDropdown
    v-model="isOpen"
    :offset="[13, 0]"
    class="notification-dropdown"
    stick-to-edges
    :close-on-content-click="false"
  >
    <template #anchor>
      <VaButton preset="secondary" color="sidebar-text">
        <VaBadge overlap color="accent" :text="badgeText">
          <VaIconNotification class="notification-dropdown__icon" />
        </VaBadge>
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[420px] sm:h-auto">
      <section class="sm:max-h-[320px] p-4 overflow-auto">
        <div v-if="isLoadingList" class="flex justify-center py-6">
          <VaProgressCircle indeterminate size="small" />
        </div>

        <p v-else-if="notifications.length === 0" class="text-center text-secondary py-6">
          {{ t('notifications.empty') }}
        </p>

        <VaList v-else class="space-y-1 mb-2">
          <VaListItem
            v-for="item in notificationsWithRelativeTime"
            :key="item.id"
            class="text-base rounded cursor-pointer"
            :style="!item.is_read ? { backgroundColor: unreadBackground } : {}"
            @click="onNotificationClick(item)"
          >
            <VaListItemSection icon class="mx-0 p-0">
              <VaIcon
                :name="typeIcon(item.type)"
                :color="typeColor(item.type)"
                :title="t(`notifications.type.${item.type}`)"
              />
            </VaListItemSection>
            <VaListItemSection>
              {{ item.message }}
            </VaListItemSection>
            <VaListItemSection icon class="mx-1 flex-col items-end gap-1">
              <span class="text-xs text-secondary whitespace-nowrap">{{ item.relativeTime }}</span>
              <span
                v-if="!item.is_read"
                class="inline-block w-2 h-2 rounded-full"
                :style="{ backgroundColor: getColor('primary') }"
              ></span>
            </VaListItemSection>
          </VaListItem>
        </VaList>

        <VaButton
          v-if="unreadCount > 0"
          preset="primary"
          class="w-full"
          :loading="isMarkingAllRead"
          @click="onMarkAllRead"
        >
          {{ t('notifications.markAllRead') }}
        </VaButton>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors, useToast } from 'vuestic-ui'
import VaIconNotification from '../../../icons/VaIconNotification.vue'
import notificationsService, { AppNotification, NotificationType } from '../../../../services/notifications'
import { getApiErrorMessage } from '../../../../services/http'

const POLL_INTERVAL_MS = 60_000
const MAX_VISIBLE_NOTIFICATIONS = 15

const { t, locale } = useI18n()
const { init: notify } = useToast()
const { getColor, setHSLAColor } = useColors()
const router = useRouter()

const unreadBackground = computed(() => setHSLAColor(getColor('primary'), { a: 0.08 }))

const isOpen = ref(false)
const notifications = ref<AppNotification[]>([])
const isLoadingList = ref(false)
const isMarkingAllRead = ref(false)
const unreadCount = ref(0)

const badgeText = computed(() => {
  if (unreadCount.value <= 0) return ''
  return unreadCount.value > 9 ? '9+' : String(unreadCount.value)
})

const typeIcon = (type: NotificationType) => (type === 'overdue' ? 'mso-error' : 'mso-schedule')
const typeColor = (type: NotificationType) => (type === 'overdue' ? 'danger' : 'warning')

const refreshUnreadCount = async () => {
  try {
    unreadCount.value = await notificationsService.getUnreadCount()
  } catch {
    // Silently ignored — this runs unattended on a timer, a toast every minute would be noise.
  }
}

const loadNotifications = async () => {
  isLoadingList.value = true
  try {
    const response = await notificationsService.list(1)
    notifications.value = response.data.slice(0, MAX_VISIBLE_NOTIFICATIONS)
  } catch (e) {
    notify({ message: getApiErrorMessage(e, t('notifications.loadError')), color: 'danger' })
  } finally {
    isLoadingList.value = false
  }
}

watch(isOpen, (open) => {
  if (open) {
    loadNotifications()
    refreshUnreadCount()
  }
})

let pollTimer: ReturnType<typeof setInterval> | undefined

onMounted(() => {
  refreshUnreadCount()
  pollTimer = setInterval(refreshUnreadCount, POLL_INTERVAL_MS)
})

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer)
})

const onNotificationClick = async (item: AppNotification) => {
  if (!item.is_read) {
    try {
      await notificationsService.markAsRead(item.id)
      item.is_read = true
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    } catch (e) {
      notify({ message: getApiErrorMessage(e, t('notifications.markReadError')), color: 'danger' })
    }
  }

  if (item.invoice) {
    isOpen.value = false
    router.push({ name: 'invoices' })
  }
}

const onMarkAllRead = async () => {
  isMarkingAllRead.value = true
  try {
    await notificationsService.markAllAsRead()
    notifications.value = notifications.value.map((item) => ({ ...item, is_read: true }))
    unreadCount.value = 0
    notify({ message: t('notifications.markAllReadSuccess'), color: 'success' })
  } catch (e) {
    notify({ message: getApiErrorMessage(e, t('notifications.markAllReadError')), color: 'danger' })
  } finally {
    isMarkingAllRead.value = false
  }
}

const TIME_NAMES = {
  second: 1000,
  minute: 1000 * 60,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
  week: 1000 * 60 * 60 * 24 * 7,
  month: 1000 * 60 * 60 * 24 * 30,
  year: 1000 * 60 * 60 * 24 * 365,
}

const getTimeName = (differenceTime: number) => {
  return Object.keys(TIME_NAMES).reduce(
    (acc, key) => (TIME_NAMES[key as keyof typeof TIME_NAMES] < differenceTime ? key : acc),
    'second',
  ) as keyof typeof TIME_NAMES
}

const notificationsWithRelativeTime = computed(() => {
  const rtf = new Intl.RelativeTimeFormat(locale.value, { style: 'short' })

  return notifications.value.map((item) => {
    const timeDifference = Math.max(0, Math.round(new Date().getTime() - new Date(item.created_at).getTime()))
    const timeName = getTimeName(timeDifference)

    return {
      ...item,
      relativeTime: rtf.format(-1 * Math.round(timeDifference / TIME_NAMES[timeName]), timeName),
    }
  })
})
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}
</style>
